import React, { useState, useEffect, useRef } from "react";
import {
  Check,
  Phone,
  Wifi,
  MessageSquare,
  BarChart3,
  Headphones,
  Clock,
  Edit,
  Download,
  FileText,
  Shield,
  Smartphone,
  Mail,
} from "lucide-react";
import html2pdf from "html2pdf.js";
import QuoteProgressService from "../services/quoteProgressService";
import {
  getSystemConfig,
  sendQuotationEmail,
} from "../services/systemConfigService";
import {
  saveQuotationPdf,
  getQuotationsByConnection,
  updateQuotationNotes,
  upsertQuotationNotes
} from "../services/quotationService";
const TelecomQuotation = (selectedConnectionId) => {
  const quotationRef = useRef();
  const connectionId = selectedConnectionId?.selectedConnectionId ?? 0;

  const [quotationData, setQuotationData] = useState(null);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailStatus, setEmailStatus] = useState(null); // 'sending', 'success', 'error'
  const [managerEmail, setManagerEmail] = useState("");

  const [isEditingEmail, setIsEditingEmail] = useState(false);
  const [tempEmail, setTempEmail] = useState("");
  const [quotationNotes, setQuotationNotes] = useState("");
  const [savedQuotations, setSavedQuotations] = useState([]);
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  useEffect(() => {
    const loadQuotations = async () => {
      try {
        const result = await getQuotationsByConnection(connectionId);
        if (result.success) {
          
          setSavedQuotations(result.data);
          // Load latest notes
          const latest = result.data.find((q) => q.is_latest);
          if (latest?.notes) {
            setQuotationNotes(latest.notes);
          }
        }
      } catch (error) {
        console.error("Failed to load quotations:", error);
      }
    };

    if (connectionId) {
      loadQuotations();
    }
  }, [connectionId]);
  useEffect(() => {
    const fetchConfigData = async () => {
      try {
        const configData = await getSystemConfig(connectionId);
        console.log("configData", configData);
        const mappedData = mapDbDataToQuotationData(configData.data);
        console.log(mappedData);
        if (mappedData) {
          setQuotationData(mappedData);
        }
        if (configData.data.director_email) {
          setManagerEmail(configData.data.director_email);
        }
      } catch (error) {
        console.error("Failed to load config:", error);
      }
    };

    fetchConfigData();
  }, [connectionId]);

  const findHardware = (hardwareItems, namePart) => {
    const item = hardwareItems?.find((h) => h.name.includes(namePart));
    if (!item) return null;
    const qty = item.quantity || 0;
    const focQty = item.focQuantity || 0;
    // Return if either quantity or focQuantity is non-zero
    if (qty === 0 && focQty === 0) return null;
    return {
      quantity: qty,
      focQuantity: focQty,
    };
  };
  const mapDbDataToQuotationData = (dbData) => {
    if (!dbData) return null;
    const aiEnabled = Boolean(dbData.leaseCalculatorData?.ai_receptionist_enabled);
    const newCosts = {
      equipmentRental: dbData.leaseCalculatorData?.adjustedRental || 0,
      estimatedCallCharges: dbData.leaseCalculatorData?.estimatedCallCharges || 0,
      mobileSimCharges:
        dbData.leaseCalculatorData?.estimated_mobile_charges || 0,
      serviceCharges: dbData.leaseCalculatorData?.new_service_charges || 0,
      networkCredit:
        dbData.leaseCalculatorData?.networkSettlementContribution || 0,
      leasedLine: dbData.leaseCalculatorData?.leased_line || 0,
      digitalServices:
        dbData.leaseCalculatorData?.digital_services?.totalNewDigitalCost || 0, // ✅ digital services cost included in new total
      itServiceCharge: dbData.leaseCalculatorData?.it_service_charge || 0, 
      itServicesTotal: dbData.itServicesTotal || dbData.leaseCalculatorData?.it_services_total || 0, // ✅ Microsoft licences / IT support resale total
      aiReceptionist: dbData.leaseCalculatorData?.ai_receptionist_enabled? Number(dbData.leaseCalculatorData?.ai_receptionist_charge) || 0 : 0,
     
    };

    // ✅ Calculate total cost as sum of values (now includes digital services)
    const newTotal = Object.values(newCosts).reduce(
      (sum, val) => sum + (val || 0),
      0,
    );

    const currentCosts = {
      mobileCharges: dbData.leaseCalculatorData?.current_lease_rental || 0,
      callCharges: dbData.leaseCalculatorData?.current_mobile_charges || 0,
      equipmentRental: dbData.leaseCalculatorData?.current_call_charges || 0,
      serviceCharges: dbData.leaseCalculatorData?.current_service_charges || 0,
      maintenance: dbData.leaseCalculatorData?.current_maintenance || 0,
      otherCharges:dbData.leaseCalculatorData?.otherCharges || 0,
      currentTotal: dbData.leaseCalculatorData?.current_total_cost || 0,
      currentOtherCharges:dbData.serviceCharges.utilities || 0,
      currentServiceCharge:
        dbData.leaseCalculatorData?.digital_services?.webHostingCost || 0,
    };

    // ✅ Difference between new and current total
    const costDifference =
      newTotal -
      (currentCosts.currentTotal + currentCosts.currentServiceCharge);
    const hi = dbData.hardwareItems || [];
    const linesFoc = dbData.linesFoc || {};
    return {
      quoteNumber: dbData?.id ? `Q-${dbData?.id}` : "",
      date: new Date().toLocaleDateString(),
      customerName: dbData.connection_name || "",

      handsets: (() => {
        const h = {};
        const t88 = findHardware(hi, "YEALINK T88"); if (t88) h["YEALINK T88"] = t88;
        const t87 = findHardware(hi, "YEALINK T87");         if (t87) h["YEALINK T87"] = t87;
        const t85 = findHardware(hi, "YEALINK T85");         if (t85) h["YEALINK T85"] = t85;
        const t58 = findHardware(hi, "T58W");         if (t58) h["T58W"] = t58;
        const t57w = findHardware(hi, "T57W");       if (t57w) h["YEALINK T57W"] = t57w;
        const t54w = findHardware(hi, "T54W");       if (t54w) h["YEALINK T54W"] = t54w;
        const dect = findHardware(hi, "Yealink W73H DECT"); if (dect) h["Yealink W73H DECT"] = dect;
        const headset = findHardware(hi, "Headsets"); if (headset) h["WIRELESS HEADSET"] = headset;
        const gs3470 = findHardware(hi, "Grandstream 3470"); if (gs3470) h["GRANDSTREAM 3470"] = gs3470;
        const gs3480 = findHardware(hi, "Grandstream 3480"); if (gs3480) h["GRANDSTREAM 3480"] = gs3480;
        const cisco = findHardware(hi, "Cisco 9871"); if (cisco) h["CISCO 9871"] = cisco;
        return h;
      })(),

     lines: {
        "SIP TRUNKS":               { quantity: dbData.currentLines?.hostedLifetimeLicenses || 0, focQuantity: linesFoc.hostedLifetimeLicensesFocQty || 0 },
        "ANALOGUE PSTN LINE":       { quantity: dbData.currentLines?.analoguePstnLines || 0,       focQuantity: 0 },
        "FTTC / FTTP FIBRE BROADBAND": { quantity: dbData.currentLines?.adslFttcBroadband || 0,   focQuantity: 0 },
        "4G / 5G BROADBAND":        { quantity: dbData.currentLines?.new4g5gBroadband || 0,        focQuantity: linesFoc.new4g5gBroadbandFocQty || 0 },
        "SoGEA BROADBAND":          { quantity: dbData.currentLines?.fttpSogeaBroadband || 0,      focQuantity: linesFoc.fttpSogeaBroadbandFocQty || 0 },
        "STARLINK BROADBAND":       { quantity: dbData.dataBroadband?.starlinkBroadband || 0,      focQuantity: linesFoc.starlinkBroadbandFocQty || 0 },
        "LEASED LINES":             { quantity: dbData.dataBroadband?.leasedLines || 0,             focQuantity: linesFoc.leasedLinesFocQty || 0 },
      },

      currentCosts,
      newCosts,
      newTotal,
      costDifference,

      contractTerm: parseInt(dbData.leaseCalculatorData?.paymentProfile) || 0,

      // ✅ Full digital_services object — used by addendum and digital services section
      digitalServices: dbData.leaseCalculatorData?.digital_services ?? null,

      // ✅ Raw leaseCalculatorData — used by DigitalServicesQuoteSection
      leaseCalculatorData: dbData.leaseCalculatorData ?? null,
      itServicesTotal: dbData.itServicesTotal || dbData.leaseCalculatorData?.it_services_total || 0,
      //aiReceptionist: aiEnabled ? (Number(dbData.leaseCalculatorData?.ai_receptionist_charge) || 10) : 0,
      aiReceptionistEnabled: aiEnabled,
    };
  };

 

  const includedSoftware = [
    "*UNLIMITED CALL PACKAGE*",
    "CALLSWITCH DESKTOP CTI",
    "CALL RECORDING",
    "CALL REPORTING",
    "ON HOLD MARKETING",
    "MOBILE APPS",
    "VOICEMAIL TO EMAIL",
  ];

  const systemIncludes = [
    "LINE MIGRATIONS & NUMBER PORTING",
    "MIGRATION OR NEW BROADBAND SERVICE",
    "FULL INSTALLATION & TRAINING",
    "NO DEPOSITS OR UPFRONT COSTS",
    "SETTLEMENT OF CURRENT CONTRACTS",
  ];

  const services = [
    {
      title: "Broadband",
      icon: Wifi,
      description:
        "Is your business suffering from an unreliable or slow internet connection? Conducting site surveys before installation lets us provide you with the highest possible speeds for your business broadband internet connection. We provide all broadband types, including ADSL, FTTC, EFM, and leased lines.",
    },
    {
      title: "Utilities",
      icon: FileText,
      description:
        "Wouldn't it be great to get a business utility plan based on your needs with fixed prices that are easy to understand and competitive? Our strong purchasing power is built on our positive relationships with major suppliers. That is why thousands of businesses across the UK are choosing us for their gas, electricity, and water. Few businesses have the time to shop around for the best deal. We can do that for you.",
    },
  ];
  const services_extra = [
    {
      title: "Business Mobiles",
      icon: Smartphone,
      description:
        "Business mobiles are an essential part of working life. We save our customers an average of 30% on their business mobile contracts. Sustaining business communication outside of your office is one of the most critical elements of modern-day business. Our range of business mobiles and tablets, coupled with industry-leading tariffs, will keep you connected.",
    },
    {
      title: "CCTV",
      icon: Shield,
      description:
        "Incorporating a CCTV system into your business can create a safer environment for your employees and customers. You can monitor your sales floor, till, or business premises, providing a deterrent against theft, violence, and vandalism. A CCTV security system can provide evidence in the event of prosecution by police or an insurance claim.",
    },
  ];
  const exportToPDF = async () => {
    const element = quotationRef.current;

    const opt = {
      margin: 0.5,
      filename: `quotation-${quotationData.quoteNumber}.pdf`,
      image: { type: "jpeg", quality: 0.98 },
      html2canvas: { scale: 2 },
      jsPDF: { unit: "in", format: "a4", orientation: "portrait" },
      pagebreak: { mode: ["avoid-all", "css", "legacy"] },
    };

    // Generate PDF blob
    const pdfBlob = await html2pdf().set(opt).from(element).outputPdf("blob");

    // Download for user
    html2pdf().set(opt).from(element).save();

    // Save to backend
    try {
      setIsSaving(true);
      await saveQuotationPdf({
        connectionId,
        quoteNumber: quotationData.quoteNumber,
        pdfBlob,
        notes: quotationNotes,
        metadata: {
          customerName: quotationData.customerName,
          totalCost: quotationData.newTotal,
          contractTerm: quotationData.contractTerm,
        },
      });

      await QuoteProgressService.saveStep({
        quoteId: connectionId,
        stepName: "quote",
        completed: true,
        actions: { downloaded: true },
      });

      alert("Quotation saved successfully!");
    } catch (error) {
      console.error("Failed to save quotation:", error);
      alert("Downloaded locally, but failed to save to database");
    } finally {
      setIsSaving(false);
    }
  };

  // Save notes handler
  const handleSaveNotes = async () => {
  try {
    const latest = savedQuotations.find((q) => q.is_latest);

    if (latest) {
      // PDF already exists — update notes on that record
      await updateQuotationNotes(latest.id, quotationNotes);
    } else {
      // No PDF yet — create/update a notes-only record
      const result = await upsertQuotationNotes({
        connectionId,
        quoteNumber: quotationData?.quoteNumber ?? `Q-${connectionId}`,
        notes: quotationNotes,
      });

      if (result.success) {
        // Keep savedQuotations in sync so future saves use updateQuotationNotes
        setSavedQuotations([result.data]);
      }
    }

    alert("Notes saved successfully!");
    setShowNotesModal(false);
  } catch (error) {
    console.error("Failed to save notes:", error);
    alert("Failed to save notes");
  }
  };

  const sendEmailWithPDF = async () => {
    if (!managerEmail) {
      alert("No manager email found in the system.");
      return;
    }

    setEmailStatus("sending");

    try {
      const element = quotationRef.current;
      const opt = {
        margin: 0.5,
        filename: `quotation-${quotationData.quoteNumber}.pdf`,
        image: { type: "jpeg", quality: 0.98 },
        html2canvas: { scale: 2 },
        jsPDF: { unit: "in", format: "a4", orientation: "portrait" },
        pagebreak: { mode: ["avoid-all", "css", "legacy"] },
      };
      const pdfBlob = await html2pdf().set(opt).from(element).outputPdf("blob");

      const formData = new FormData();
      formData.append(
        "pdf",
        pdfBlob,
        `quotation-${quotationData.quoteNumber}.pdf`,
      );
      formData.append("to", managerEmail);
      formData.append(
        "subject",
        `Telecom Quotation - ${quotationData.quoteNumber}`,
      );
      formData.append(
        "body",
        `Please find attached the quotation for ${quotationData.customerName}.`,
      );

      // ✅ Required for the backend to create the QuoteActivityLog row
      // (and tag the email with email_log_id for SendGrid webhook tracking)
      if (connectionId) {
        formData.append("connection_id", String(connectionId));
      }
      formData.append("document_type", "quote");

      const response = await sendQuotationEmail(formData);

      if (response.success) {
        setEmailStatus("success");

        // Save PDF to backend (same as download flow)
        await saveQuotationPdf({
          connectionId,
          quoteNumber: quotationData.quoteNumber,
          pdfBlob,
          notes: quotationNotes,
          metadata: {
            customerName: quotationData.customerName,
            totalCost: quotationData.newTotal,
            contractTerm: quotationData.contractTerm,
          },
        });

        await QuoteProgressService.saveStep({
          quoteId: connectionId,
          stepName: "quote",
          completed: true,
          actions: { emailed: true },
        });
        setTimeout(() => {
          setShowEmailModal(false);
          setEmailStatus(null);
        }, 2000);
      } else {
        throw new Error(response?.message || "Failed to send email");
      }
    } catch (error) {
      console.error("Error sending email:", error?.response?.data || error);
      setEmailStatus("error");
    }
  };

  const handleEditEmail = () => {
    setIsEditingEmail(true);
    setTempEmail(managerEmail);
  };

  const handleSaveEmail = () => {
    if (tempEmail && validateEmail(tempEmail)) {
      setManagerEmail(tempEmail);
      setIsEditingEmail(false);
    } else {
      alert("Please enter a valid email address");
    }
  };

  const handleCancelEdit = () => {
    setIsEditingEmail(false);
    setTempEmail(managerEmail);
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };
   const isSystemConfigEmpty = () => {
    if (!quotationData) return true;

    const hasHandsets = Object.keys(quotationData.handsets || {}).length > 0;
    const hasLines = Object.values(quotationData.lines || {}).some(
      (l) => l.quantity > 0 || l.focQuantity > 0
    );
    const hasCosts = (quotationData.newTotal || 0) > 0;

    return !hasHandsets && !hasLines && !hasCosts;
  };

  const QtyCell = ({ quantity, focQuantity }) => {
    const foc = focQuantity || 0;
    const total = (quantity || 0) + foc;
    return (
      <td className="p-3 text-center">
        {total}
        
      </td>
    );
  };

  const QtyCellBordered = ({ quantity, focQuantity }) => {
    const foc = focQuantity || 0;
    const total = (quantity || 0) + foc;
    return (
      <td className="border border-gray-600 p-3 text-center">
        {total}
        
      </td>
    );
  };

  return (
    <div className="max-w-4xl mx-auto bg-white">
      {/* Export Buttons */}

      <div className="flex justify-end mb-4 gap-2">
        <button
          onClick={() => setShowNotesModal(true)}
          className="bg-gray-600 text-white px-4 py-2 rounded-lg flex items-center gap-2"
        >
          <FileText size={16} /> Add Notes
        </button>

        <button
          onClick={exportToPDF}
          disabled={isSaving}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 disabled:opacity-50"
        >
          {isSaving ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              Saving...
            </>
          ) : (
            <>
              <Download size={16} /> Download & Save PDF
            </>
          )}
        </button>

        <button
          onClick={() => {
            if (isSystemConfigEmpty()) {
              alert(
                "System configuration is not present for this connection. Please go back and add the system configuration first."
              );
              return;
            }
            setShowEmailModal(true);
          }}
          className="bg-green-600 text-white px-4 py-2 rounded-lg flex items-center gap-2"
        >
          <Mail size={16} /> Email PDF
        </button>
      </div>

      {/* Email Confirmation Modal */}
      {showEmailModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-md mx-auto">
            <h3 className="text-xl font-bold text-gray-800 mb-4">
              Confirm Email
            </h3>

            {emailStatus === null && (
              <>
                <div className="mb-4">
                  <p className="mb-2">The quotation PDF will be sent to:</p>

                  {isEditingEmail ? (
                    <div className="space-y-3">
                      <input
                        type="email"
                        value={tempEmail}
                        onChange={(e) => setTempEmail(e.target.value)}
                        className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Enter email address"
                      />
                      <div className="flex gap-2">
                        <button
                          onClick={handleSaveEmail}
                          className="flex-1 bg-blue-600 text-white py-2 rounded-md hover:bg-blue-700 transition-colors"
                        >
                          Save
                        </button>
                        <button
                          onClick={handleCancelEdit}
                          className="flex-1 bg-gray-500 text-white py-2 rounded-md hover:bg-gray-600 transition-colors"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between bg-blue-50 p-3 rounded-lg">
                      <p className="font-semibold text-blue-600 break-all">
                        {managerEmail || "No email found"}
                      </p>
                      <button
                        onClick={handleEditEmail}
                        className="ml-2 p-1 text-blue-600 hover:bg-blue-100 rounded transition-colors"
                        title="Edit email"
                      >
                        <Edit size={16} />
                      </button>
                    </div>
                  )}
                </div>

                <div className="flex justify-end gap-3">
                  <button
                    onClick={() => {
                      setShowEmailModal(false);
                      setIsEditingEmail(false);
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-100 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={sendEmailWithPDF}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center gap-2 transition-colors disabled:opacity-50"
                    disabled={!managerEmail || isEditingEmail}
                  >
                    <Mail size={16} /> Send Email
                  </button>
                </div>
              </>
            )}

            {emailStatus === "sending" && (
              <div className="text-center py-4">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                <p>Sending email...</p>
              </div>
            )}

            {emailStatus === "success" && (
              <div className="text-center py-4">
                <Check className="text-green-500 w-12 h-12 mx-auto mb-4" />
                <p className="text-green-600 font-medium">
                  Email sent successfully!
                </p>
              </div>
            )}

            {emailStatus === "error" && (
              <div className="text-center py-4">
                <p className="text-red-600 font-medium mb-4">
                  Failed to send email. Please try again.
                </p>
                <button
                  onClick={() => setEmailStatus(null)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                >
                  Try Again
                </button>
              </div>
            )}
          </div>
        </div>
      )}
      {showNotesModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-2xl mx-auto">
            <h3 className="text-xl font-bold text-gray-800 mb-4">
              Quotation Notes
            </h3>

            <textarea
              value={quotationNotes}
              onChange={(e) => setQuotationNotes(e.target.value)}
              className="w-full h-40 p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Add notes about this quotation..."
            />

            <div className="flex justify-end gap-3 mt-4">
              <button
                onClick={() => setShowNotesModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveNotes}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Save Notes
              </button>
            </div>
          </div>
        </div>
      )}
      <div ref={quotationRef} className="p-8">
        {/* Header */}
        <div className="text-center mb-8">
          <img
            src="/images/fibre.png"
            className="mx-auto w-full max-w-4xl h-auto rounded-lg shadow-lg"
            alt="Phone system illustration"
          />
        </div>

        {/* About Us Section */}

        <div className=" mb-8">
          <h3 className="text-2xl font-bold text-blue-900 mb-4">ABOUT US</h3>
          {/* Who is Fibre Telecoms */}
          <div className="mb-6">
            <h4 className="font-bold text-blue-800 mb-2">
              Who is Fibre Telecoms…...?
            </h4>
            <p className="text-sm text-gray-700 mb-4">
              Fibre Telecoms UK is a VoIP and hosted phone solutions market
              leader. We pride ourselves on providing the highest reliability
              and value for money. Fibre is passionate about your business
              needs, from profitability to your customers perceptions of you.
            </p>
          </div>

          {/* Full-width Image */}
          <img
            src="/images/aboutus.png"
            alt="About Fibre Telecoms"
            className="w-full h-auto rounded-lg shadow-lg mb-6"
          />

          {/* Our Experience */}
          <div className="flex flex-col md:flex-row md:items-center md:gap-6 print:flex print:flex-row print:items-center">
            {/* Left side (text) */}

            {/* Our Experience */}
            <div className="mb-8">
              <h4 className="font-bold text-blue-800 mb-2">Our Experience</h4>

              {/* Full width intro paragraphs */}
              <p className="text-sm text-gray-700 mb-4">
                We have over 30 years of experience providing award-winning
                phone and CCTV systems from some of the industry's biggest
                names, including Grandstream, LG, Yealink, and Hikvision. Our
                services include broadband, utilities, network cabling and
                business mobiles.
              </p>
              <p className="text-sm text-gray-700 mb-4">
                Customer care is our highest priority, and our staff always
                provide the best possible care and attention.
              </p>
              <p className="text-sm text-gray-700 mb-4">
                Following BT's announcement regarding turning off ISDN/PSTN
                lines, there has never been a better time to future-proof your
                business. We are the leading specialists in internet IP solution
                phone systems and provide the best possible broadband speeds.
              </p>

              {/* Grid: left (remaining text), right (image) */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
                {/* Left side */}
                <div className="[text-align:justify] space-y-4">
                  <p className="text-sm text-gray-700">
                    Our experienced team check your lines and broadband speeds
                    before we come out to you, so we can provide the best phone
                    setup, including hybrid digital-lP phone system or 4G
                    connections where your broadband speeds are not up to the
                    task.
                  </p>
                  <p className="text-sm text-gray-700">
                    We believe that all businesses should have access to the
                    latest and most efficient features, including call
                    recording, computer-integration software, and mobile apps.
                    Our phone systems include these and many other features.
                  </p>
                </div>

                {/* Right side */}
                <div>
                  <img
                    src="/images/experience.png"
                    alt="About Fibre Telecoms"
                    className="w-full h-auto rounded-lg shadow-lg"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Our Handsets Section - Now on new page */}
        <div className=" flex flex-col md:flex-row items-center gap-4 mb-8 print:flex print:flex-row">
          {/* Left: Image */}
          <div className="md:w-1/3 print:w-1/3">
            <img
              src="/images/handset.png"
              alt="Handset"
              className="w-full h-auto rounded-2xl shadow-lg"
            />
          </div>

          {/* Right: Text Box */}
          <div className="md:w-2/3 print:w-2/3 bg-blue-800 text-white p-6 rounded-2xl shadow-lg avoid-page-break">
            <h3 className="text-xl font-bold mb-3">Our Handsets</h3>
            <p className="mb-4">
              Your business operates in its own unique way, which is why we
              offer a variety of business phone handsets to suit the way you
              work.
            </p>
            <p>
              Whether it's an ergonomically designed desk phone or a cordless
              phone to allow you to be contactable throughout your office, we
              have the phones to meet your business needs.
            </p>
          </div>
        </div>
       
        <img
          src="/images/T88quote.png"
          alt="Handset"
          className="w-full h-auto mx-auto rounded-2xl shadow-lg mb-8"
        />
        {/* Yealink */}
        <div className="flex flex-col md:flex-row md:items-center md:gap-6 print:flex print:flex-row print:items-center print:gap-8">
          {/* Left side (text) */}
          <div className="md:w-1/2 print:w-1/2 text-center md:text-left print:mb-4">
            <h4 className="font-bold text-blue-800 mb-2">YEALINK T88W</h4>
            <p className="text-sm text-gray-700 mb-4 [text-align:justify]">
              The Yealink T88W Pro WiFi VoIP/SIP Phone is a simple-to-use
              smart business phone that provides a HD audio and video-calling
              experience for business professionals. This smart business phone
              enables productivity-enhancing visual communication with the ease
              of a standard phone. This phone supports the Yealink Bluetooth
              handset BTH88, with which users are free to move around when on a
              call – no more missed calls when they leave their desk.
            </p>
            <p className="text-sm text-gray-700 mb-4 [text-align:justify]">
              Runs Android 9.0. 7-inch (1024 x 600) capacitive adjustable touch
              screen. Supports Yealink Bluetooth Handset BTH58. 720p30 HD video.
              Optimal HD audio, Yealink Noise Proof Technology. Yealink’s SDK
              (Software Development Kit). DoorPhone Features (Preview,
              One-button Open, Monitoring). Built-in Bluetooth 4.2 and Dual Band
              2.4GHz/5GHz WiFi.
            </p>
          </div>

          {/* Right side (image) */}
          <div className="md:w-1/2 print:w-1/2 text-center md:text-left print:mt-8">
            <p className="text-sm text-gray-700 mb-4 [text-align:justify]">
              With a range of up to 10 metres (30 feet), the Bluetooth handset
              offers more office flexibility for users. Based on the Android 9.0
              operating system, the SIP-T88W Pro with camera uses a new user
              interface, greatly improving users' work efficiency. Furthermore,
              the SIP-T88W Pro with camera integrates with IP video intercom
              systems from major vendors in the market, delivering true
              cost-efficiency to customers.
            </p>
            <p className="text-sm text-gray-700 mb-4 [text-align:justify]">
              Opus Codec support. Up to 16 SIP accounts. Up to 3-party video
              conferencing. Up to 10-party video/audio mixed conferencing (up to
              2 video calls). Dual-port Gigabit Ethernet, supports PoE. Wall
              mountable. Supports up to three (3) Expansion Module EXP50.
              Supports Yealink/Unify Square Device Management Platform.
            </p>
          </div>
        </div>

        <div className="avoid-page-break">
          <div className="page-break-before flex flex flex-col md:flex-row md:items-center md:gap-6 print:flex print:flex-row print:items-center print:gap-8">
            {/* Left side (text) */}
            <div className="md:w-1/2 print:w-1/2 text-center md:text-left print:mb-4">
              <img
                src="/images/yealinkw56h.png"
                alt="Yealink W56H"
                className="max-w-xs w-full h-auto mx-auto mb-4"
              />
              <h4 className="font-bold text-blue-800 mb-2">YEALINK W56H</h4>

              <p className="text-sm text-gray-700 mb-4 [text-align:justify]">
                Combining the benefits of wireless communication with rich
                business features of Voiceover-IP telephony, the Yealink W56H
                cordless DECT handset empowers users with convenience of
                wireless communication that users can enjoy conversations in
                excellent speech quality, benefit from freedom of movement and
                its professional features. The attractive design with 2.4"
                colour screen underlined by its modern look is what defines this
                state-of-the-art VoIP wireless telephone.
              </p>
            </div>

            {/* Right side (image) */}
            <div className="md:w-1/2 print:w-1/2 text-center md:text-left print:mt-8">
              <img
                src="/images/T87W.jpg"
                alt="Yealink T87W"
                className="max-w-xs w-full h-auto mx-auto mb-4 print:max-w-[200px]"
              />
              <h4 className="font-bold text-blue-800 mb-2">YEALINK T87</h4>
              <p className="text-sm text-gray-700 mb-4 [text-align:justify]">
                The Yealink SIP-T87 VoIP/SIP Phone as a feature-rich business
                tool for excellent communications and extended functionality.
                With many built-in programmable keys, this 12-line IP phone
                guarantees enhanced productivity. Yealink Optima HD Voice
                technology and wideband codec of Opus gives you superb audio
                quality and crystal-clear voice communications. Moreover, the
                SIP-T87W has dual USB ports allowing you to use adapters for
                Bluetooth and Wi-Fi, and also to use USB headsets and USB
                recording devices.
              </p>
            </div>
          </div>

          <div className="flex flex-col md:flex-row md:items-center md:gap-6 print:flex print:flex-row print:items-center print:gap-4">
            {/* Left side (YEALINK T57W) */}
            <div className="md:w-1/2 print:w-1/2 text-center md:text-left print:mb-2">
              <img
                src="/images/yealinkt85.jpg"
                alt="Yealink T85"
                className="max-w-xs w-full h-auto mx-auto mb-2 print:max-w-[170px] print:mb-2"
              />
              <h4 className="font-bold text-blue-800 mb-1 print:mb-1">
                YEALINK T85
              </h4>
              <p className="text-xs text-gray-700 mb-2 text-justify print:text-[15px] print:leading-tight">
                The Yealink SIP-T85 VolP/S1P Phone presents a dynamic business
                communication solution to executives and professionals. The
                phone has a large touchscreen that makes the switch between
                pages and applications swift, easy and convenient. With Yealink
                Optima HD Voice technology and wideband codec of Opus, the
                SIP-T85 gives you superb audio quality and crystal-clear voice
                communications. Moreover, the SIP-T88 has dual USB ports
                allowing you to use adapters for Bluetooth and Wi-Fi, and also
                to use USB headsets and USB recording devices.
              </p>
            </div>

            {/* Right side (image) */}
            <div className="md:w-1/2 print:w-1/2 text-center md:text-left print:mt-2">
              <img
                src="/images/yealinkw53h.png"
                alt="Yealink W53H"
                className="max-w-xs w-full h-auto mx-auto mb-2 print:max-w-[180px] print:mb-2"
              />
              <h4 className="font-bold text-blue-800 mb-1 print:mb-1">
                YEALINK W53H
              </h4>
              <p className="text-xs text-gray-700 mb-2 text-justify print:text-[14px] print:leading-tight">
                The Yealink W53H VolP/S1P DECT Phone/Handset is a
                next-generation wireless handset designed for business use, with
                excellent battery life and high compatibility. Combining the
                benefits of wireless communication with rich business features
                of Voice-over-IP telephony, it empowers users with the
                convenience of wireless communication. This means that users can
                enjoy conversations in excellent speech quality with the benefit
                from freedom of movement, in addition to the W53H's professional
                features. The compact and attractive design, with 1.8" TFT
                colour screen underlined by its modern look, is what defines
                this state-of-the-art VolP wireless telephone{" "}
              </p>
            </div>
          </div>
        </div>
        {/* Software Applications Section */}
        <div className=" new-page mb-8 p-6 bg-blue-50 rounded-lg">
          <div className="space-y-2 text-center">
            <img
              src="/images/power.png"
              alt="Yealink W56H"
              className="max-h-40 w-auto mx-auto mb-4"
            />
            <h4 className="text-xl font-bold text-blue-900">
              FULLY INCLUDED WITH EVERY SYSTEM
            </h4>
            <h2 className="text-2xl font-bold text-gray-900">
              Software And Applications
            </h2>

            <p className="text-center text-gray-700 mb-4 max-w-3xl mx-auto text-sm leading-tight">
              We offer a range of business-enhancing applications and software
              to help you get from your new business phone system and make life
              easier, smoother and more productive.
            </p>

            {/* 3-column grid, auto-responsive */}
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
              {/* Item 1 */}
              <div className="text-center">
                <img
                  src="/images/mobile.png"
                  alt="Mobile Apps"
                  className="w-10 h-10 mx-auto mb-3"
                />
                <h4 className="font-bold text-blue-800 mb-2">
                  Mobile Phone Apps
                </h4>
                <p className="text-sm text-gray-600 text-justify">
                  One of our most popular applications is the system mobile app
                  that links your desk phone straight to your smartphone! You
                  can seamlessly divert office calls to your mobile by logging
                  into the app. Calling from the app displays your main office
                  number, so you never need to give out your mobile number
                  again. Better still, your calls are still recorded.
                </p>
              </div>

              {/* Item 2 */}
              <div className="text-center">
                <img
                  src="/images/system.png"
                  alt="System - SMS Text Service"
                  className="w-10 h-10 mx-auto mb-3"
                />
                <h4 className="font-bold text-blue-800 mb-2">
                  System - SMS Text Service
                </h4>
                <p className="text-sm text-gray-600 text-justify">
                  Send SMS messages to your customers with our unique
                  self-service platform. Personalise your SMS texts with our
                  cutting-edge tools.
                </p>
              </div>

              {/* Item 3 */}
              <div className="text-center">
                <img
                  src="/images/cti.png"
                  alt="Computer Telephone Integration (CTI)"
                  className="w-10 h-10 mx-auto mb-3"
                />
                <h4 className="font-bold text-blue-800 mb-2">
                  Computer Telephone Integration (CTI)
                </h4>
                <p className="text-sm text-gray-600 text-justify">
                  Elevate your customer service with CTI, which links your phone
                  system with your computer and CRM platform. See the record of
                  an incoming customer on-screen as the phone rings. Find your
                  new customer's location without having to ask. All thanks to
                  CTI.
                </p>
              </div>

              {/* Item 4 */}
              <div className="text-center">
                <img
                  src="/images/callrecording.png"
                  alt="Call Recording"
                  className="w-10 h-10 mx-auto mb-3"
                />
                <h4 className="font-bold text-blue-800 mb-2">Call Recording</h4>
                <p className="text-sm text-gray-600 text-justify">
                  How often do you make a mistake while making notes on a call
                  or had information taken on a call disputed? Recording every
                  call in and out of your business provides a great fail-safe
                  and supports your team's performance, allowing them to listen
                  back to their calls.
                </p>
              </div>

              {/* Item 5 */}
              <div className="text-center">
                <img
                  src="/images/onhold.png"
                  alt="On Hold Marketing"
                  className="w-10 h-10 mx-auto mb-3"
                />
                <h4 className="font-bold text-blue-800 mb-2">
                  On Hold Marketing
                </h4>
                <p className="text-sm text-gray-600 text-justify">
                  If confronted with silence while on hold,90% of callers will
                  hang up after 40 seconds; 34% will not call back.Whether it's
                  music or a chance to promote your services, this feature
                  hugely increases your customers' perception of your business
                </p>
              </div>

              {/* Item 6 */}
              <div className="text-center">
                <img
                  src="/images/callreport.png"
                  alt="Call Reporting"
                  className="w-10 h-10 mx-auto mb-3"
                />
                <h4 className="font-bold text-blue-800 mb-2">Call Reporting</h4>
                <p className="text-sm text-gray-600 text-justify">
                  Call reporting allows you to view all incoming and outgoing
                  calls to your business, including those you missed outside
                  office hours.Call reporting allows you to identify potential
                  call flow issues and streamline your business.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Our Services Section */}
        <div className="mb-8">
          <h3 className="text-2xl font-bold text-blue-900 mb-6 text-center">
            OUR SERVICES
          </h3>
          <h4 className="text-xl font-bold text-blue-800 mb-6 text-center">
            What Else Does Fibre Do?
          </h4>
          <img
            src="/images/wifi.png"
            alt="Handset"
            className="w-full h-auto rounded-2xl shadow-lg mb-8"
          />
          <div className="grid md:grid-cols-2 gap-6">
            {services.map((service, index) => (
              <div key={index} className="p-4 border rounded-lg">
                <div className="flex items-center mb-3 ">
                  <service.icon className="w-6 h-6 text-blue-600 mr-3" />
                  <h5 className="font-bold text-blue-800">{service.title}</h5>
                </div>
                <p className="text-sm text-gray-600 text-justify">
                  {service.description}
                </p>
              </div>
            ))}
          </div>
          <img
            src="/images/cctv.png"
            alt="Handset"
            className="w-full h-auto rounded-2xl shadow-lg mb-8"
          />
          <div className="grid md:grid-cols-2 gap-6">
            {services_extra.map((service, index) => (
              <div key={index} className="p-4 border rounded-lg">
                <div className="flex items-center mb-3">
                  <service.icon className="w-6 h-6 text-blue-600 mr-3" />
                  <h5 className="font-bold text-blue-800">{service.title}</h5>
                </div>
                <p className="text-sm text-gray-600 text-justify">
                  {service.description}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Quotation Tables */}

        {!quotationData ? (
          <p>Loading quotation data...</p>
        ) : (
          <>
            <div className="new-page mb-8">
              {/* Handsets */}
              <div className="mb-6">
                {/* Handsets Table with overlapping blue header */}
                <div className="bg-gray-800 text-white relative">
                  {/* Blue quote header positioned on top (moved to right) */}
                  <div className="absolute -top-8 right-4 bg-blue-600 text-white px-12 py-3 rounded-lg z-10">
                    <h2 className="text-lg font-bold">PHONE SYSTEM QUOTE</h2>
                    <p className="text-sm">{quotationData.customerName}</p>
                  </div>

                  {/* Add padding top to accommodate the overlapping header */}
                  <div className="pt-12">
                    <div className="bg-gray-700 py-3 text-center">
                      <h3 className="text-lg font-bold">HANDSETS</h3>
                    </div>
                    <table className="w-full">
                      <thead>
                        <tr className="bg-gray-700">
                          <th className="p-3 text-left font-bold">
                            MAKE / MODEL
                          </th>
                          <th className="p-3 text-center font-bold">
                            QUANTITY
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                      
                        {Object.entries(quotationData.handsets).map(
                          ([model, data], index) => (
                            <tr
                              key={model}
                              className={
                                index % 2 === 0 ? "bg-gray-800" : "bg-gray-750"
                              }
                            >
                              <td className="p-3 text-left">{model}</td>
                              <QtyCell quantity={data.quantity} focQuantity={data.focQuantity} />
                            </tr>
                          ),
                        )}

                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              {/* Additional Software */}
              <div className="mb-6 bg-black text-white p-4 rounded-lg">
                <h3 className="text-xl font-bold text-blue-400 mb-4">
                  ADDITIONAL SOFTWARE APPLICATIONS
                </h3>
                <table className="w-full border-collapse border border-gray-600 mb-4">
                  <thead>
                    <tr className="bg-gray-800">
                      <th className="border border-gray-600 p-3 text-left">
                        DESCRIPTION
                      </th>
                      <th className="border border-gray-600 p-3 text-center">
                        INCLUDED
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {includedSoftware.map((item, index) => (
                      <tr key={index} className="bg-gray-900">
                        <td className="border border-gray-600 p-3">{item}</td>

                        <td className=" border border-gray-600 p-3 text-center text-green-400 text-xl">
                          ✔
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Line Requirements */}
              <div className="mb-6 bg-black text-white p-4 rounded-lg">
                <h3 className="text-xl font-bold text-blue-400 mb-4">
                  LINE AND BROADBAND REQUIREMENTS
                </h3>
                <table className="w-full border-collapse border border-gray-600">
                  <thead>
                    <tr className="bg-gray-800">
                      <th className="border border-gray-600 p-3 text-left">
                        DESCRIPTION
                      </th>
                      <th className="border border-gray-600 p-3 text-center">
                        QUANTITY
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                 
                  {Object.entries(quotationData.lines)
                    .filter(([, data]) => data.quantity > 0 || data.focQuantity > 0)
                    .map(([description, data]) => (
                      <tr key={description} className="bg-gray-900">
                        <td className="border border-gray-600 p-3">
                          {description}
                        </td>
                        <QtyCellBordered quantity={data.quantity} focQuantity={data.focQuantity} />
                      </tr>
                    ))}

                  </tbody>
                </table>
                <p className="text-sm text-gray-400">
                  *Unlimited call package excludes all international and premium
                  rate call destinations*
                </p>
              </div>
            </div>

            {/* Cost Comparison */}
            {/* Cost Comparison */}
            <div className=" new-page mb-8">
              <div className="bg-gray-800 text-white relative">
                {/* Overlapping blue header aligned right */}
                <div className="absolute -top-8 right-4 bg-blue-600 text-white px-12 py-3 rounded-lg z-10">
                  <h2 className="text-lg font-bold">COST COMPARISON</h2>
                </div>

                {/* Padding to accommodate header */}

                <div className="max-w-2xl mx-auto">
                  {/* Current Spend */}
                  <div className="mb-6">
                    <div className="bg-gray-700 py-3 text-center mb-2">
                      <h3 className="text-lg font-bold">
                        CURRENT SPEND PER MONTH
                      </h3>
                    </div>
                    <table className="w-full border-collapse border border-gray-600">
                      <thead>
                        <tr className="bg-gray-800">
                          <th className="border border-gray-600 p-3 text-left">
                            MAKE / MODEL
                          </th>
                          <th className="border border-gray-600 p-3 text-right">
                            QUANTITY
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="bg-gray-900">
                          <td className="border border-gray-600 p-3">
                            EXISTING EQUIPMENT RENTALS
                          </td>
                          <td className="border border-gray-600 p-3 text-right">
                            £
                            {quotationData.currentCosts.mobileCharges.toFixed(
                              2,
                            )}
                          </td>
                        </tr>
                        <tr>
                          <td className="border border-gray-600 p-3">
                            CURRENT CALL CHARGES
                          </td>
                          <td className="border border-gray-600 p-3 text-right">
                            £{quotationData.currentCosts.callCharges.toFixed(2)}
                          </td>
                        </tr>
                        <tr className="bg-gray-900">
                          <td className="border border-gray-600 p-3">
                            CURRENT EQUIPMENT RENTAL
                          </td>
                          <td className="border border-gray-600 p-3 text-right">
                            £
                            {quotationData.currentCosts.equipmentRental.toFixed(
                              2,
                            )}
                          </td>
                        </tr>
                        <tr>
                          <td className="border border-gray-600 p-3">
                            CURRENT SERVICE CHARGES
                          </td>
                          <td className="border border-gray-600 p-3 text-right">
                            £
                            {quotationData.currentCosts.serviceCharges.toFixed(
                              2,
                            )}
                          </td>
                        </tr>
                        <tr className="bg-gray-900">
                          <td className="border border-gray-600 p-3">
                            CURRENT MAINTENANCE
                          </td>
                          <td className="border border-gray-600 p-3 text-right">
                            £{quotationData.currentCosts.maintenance.toFixed(2)}
                          </td>
                        </tr>
                         <tr className="bg-gray-900">
                          <td className="border border-gray-600 p-3">
                            CURRENT OTHER COSTS
                          </td>
                          <td className="border border-gray-600 p-3 text-right">
                           £
                            {quotationData.currentCosts?.currentOtherCharges.toFixed(
                              2
                            )}
                          </td>
                        </tr>
                        <tr className="bg-gray-900">
                          <td className="border border-gray-600 p-3">
                            CURRENT DIGITAL SERVICE COSTS
                          </td>
                          <td className="border border-gray-600 p-3 text-right">
                            £
                            {quotationData.digitalServices?.webHostingCost.toFixed(
                              2
                            )}
                          </td>
                        </tr>
                        <tr className="bg-gray-700 font-bold">
                          <td className="border border-gray-600 p-3">
                            TOTAL CURRENT SPEND
                          </td>
                          <td className="border border-gray-600 p-3 text-right">
                            £
                            {(
                              quotationData.currentCosts.currentTotal +
                              (quotationData.digitalServices?.webHostingCost
                                ? parseFloat(
                                    quotationData.digitalServices
                                      .webHostingCost,
                                  )
                                : 0)
                            ).toFixed(2)}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  {/* New Costs */}
                  <div className="mb-6">
                    <div className="bg-gray-700 py-3 text-center mb-2">
                      <h3 className="text-lg font-bold">
                        FIBRE TELECOMS UK MONTHLY COSTS
                      </h3>
                    </div>
                    <div className="mb-3 p-2 bg-green-800 text-center rounded">
                      <strong>INITIAL UP FRONT COST - £0.00</strong>
                    </div>
                    <table className="w-full border-collapse border border-gray-600">
                      <tbody>
                        <tr className="bg-gray-900">
                          <td className="border border-gray-600 p-3">
                            PHONE SYSTEM EQUIPMENT RENTAL
                          </td>
                          <td className="border border-gray-600 p-3 text-right">
                            £{quotationData.newCosts.equipmentRental.toFixed(2)}
                          </td>
                        </tr>
                        <tr>
                          <td className="border border-gray-600 p-3">
                            MOBILE SIM CHARGES
                          </td>
                          <td className="border border-gray-600 p-3 text-right">
                            £
                            {quotationData.newCosts.mobileSimCharges.toFixed(2)}
                          </td>
                        </tr>
                        <tr>
                          <td className="border border-gray-600 p-3">
                            ESTIMATED CALL CHARGES
                          </td>
                          <td className="border border-gray-600 p-3 text-right">
                            £{quotationData.newCosts.estimatedCallCharges.toFixed(2)}
                          </td>
                        </tr>
                        <tr className="bg-gray-900">
                          <td className="border border-gray-600 p-3">
                            SERVICE CHARGES
                          </td>
                          <td className="border border-gray-600 p-3 text-right">
                            £{quotationData.newCosts.serviceCharges.toFixed(2)}
                          </td>
                        </tr>
                        <tr>
                          <td className="border border-gray-600 p-3">
                            MULTI SERVICE NETWORK CREDIT
                          </td>
                          <td className="border border-gray-600 p-3 text-right text-green-400">
                            £{quotationData.newCosts.networkCredit.toFixed(2)}
                          </td>
                        </tr>
                        <tr className="bg-gray-900">
                          <td className="border border-gray-600 p-3">
                            LEASED LINE
                          </td>
                          <td className="border border-gray-600 p-3 text-right">
                            £{quotationData.newCosts.leasedLine.toFixed(2)}
                          </td>
                        </tr>
                        <tr className="bg-gray-900">
                          <td className="border border-gray-600 p-3">
                            DIGITAL SERVICES
                          </td>
                          <td className="border border-gray-600 p-3 text-right">
                            £
                            {quotationData.digitalServices?.totalNewDigitalCost.toFixed(
                              2,
                            )}
                          </td>
                        </tr>
                        <tr className="bg-gray-900">
                          <td className="border border-gray-600 p-3">
                            IT SERVICE CHARGE
                          </td>
                          <td className="border border-gray-600 p-3 text-right">
                            £{quotationData.newCosts.itServiceCharge.toFixed(2)}
                          </td>
                        </tr>
                        <tr className="bg-gray-900">
                          <td className="border border-gray-600 p-3">
                            IT SERVICES / MICROSOFT LICENCES
                          </td>
                          <td className="border border-gray-600 p-3 text-right">
                           £{(quotationData.newCosts.itServicesTotal || 0).toFixed(2)}
                          </td>
                        </tr>
                        <tr className="bg-gray-900">
  <td className="border border-gray-600 p-3">
    AI RECEPTIONIST
    {quotationData.aiReceptionistEnabled && (
      <div className="text-xs text-yellow-400 mt-1">
        * Inbound calls charged at 15p per minute
      </div>
    )}
  </td>
  <td className="border border-gray-600 p-3 text-right">
    £{(quotationData.newCosts.aiReceptionist || 0).toFixed(2)}
  </td>
</tr>
                       
                        <tr className="bg-blue-800 font-bold">
                          <td className="border border-gray-600 p-3">
                            TOTAL NEW MONTHLY COST
                          </td>
                          <td className="border border-gray-600 p-3 text-right">
                            £{quotationData.newTotal.toFixed(2)}
                          </td>
                        </tr>
                        
                       
                      </tbody>
                    </table>
                  
                  </div>

                  {/* Monthly Investment */}
                  <div className="mb-6">
                    <table className="w-full border-collapse border border-gray-600">
                      <tbody>
                        <tr className="bg-green-800 font-bold">
                          <td className="border border-gray-600 p-3">
                            {quotationData.costDifference < 0
                              ? "MONTHLY SAVINGS"
                              : "MONTHLY INVESTMENT"}
                          </td>
                          <td className="border border-gray-600 p-3 text-right text-green-400">
                            £{Math.abs(quotationData.costDifference).toFixed(2)}
                          </td>
                        </tr>
                        <tr className="bg-gray-900">
                          <td className="border border-gray-600 p-3">
                            CONTRACT TERM (MONTHS)
                          </td>
                          <td className="border border-gray-600 p-3 text-right">
                            {quotationData.contractTerm}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>

            {/* Phone System Cost Includes - Updated to match dark theme */}
            <div className="mb-6 bg-black text-white p-4 rounded-lg">
              <h3 className="text-xl font-bold text-blue-400 mb-4 text-center">
                PHONE SYSTEM COST INCLUDES
              </h3>
              <table className="w-full border-collapse border border-gray-600">
                <thead>
                  <tr className="bg-gray-800">
                    <th className="border border-gray-600 p-3 text-left">
                      DESCRIPTION
                    </th>
                    <th className="border border-gray-600 p-3 text-center">
                      INCLUDED
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {systemIncludes.map((item, index) => (
                    <tr key={index} className="bg-gray-900">
                      <td className="border border-gray-600 p-3">{item}</td>
                      <td className="border border-gray-600 p-3 text-center text-green-400 text-xl">
                        ✔
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {/* Digital Services Section - shown only if digital services exist */}
            {quotationData.digitalServices &&
              quotationData.digitalServices?.totalNewDigitalCost > 0 &&
              (() => {
                const ds = quotationData.digitalServices;
                const licences = ds.licenceServices ?? {};
                const axo = ds.axoSignalPackage ?? null;

                return (
                  <div className="mb-6 bg-black text-white p-4 rounded-lg">
                    <h3 className="text-xl font-bold text-blue-400 mb-4 text-center">
                      DIGITAL SERVICES
                    </h3>
                    <table className="w-full border-collapse border border-gray-600">
                      <thead>
                        <tr className="bg-gray-800">
                          <th className="border border-gray-600 p-3 text-left">
                            SERVICE
                          </th>
                          <th className="border border-gray-600 p-3 text-left">
                            DETAIL
                          </th>
                          <th className="border border-gray-600 p-3 text-right">
                            MONTHLY COST
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {/* Wizzyl */}
                        {licences.Wizzyl > 0 && (
                          <tr className="bg-gray-900">
                            <td className="border border-gray-600 p-3 font-semibold text-blue-300">
                              Wizzyl
                            </td>
                            <td className="border border-gray-600 p-3 text-gray-300 text-sm">
                              Website Platform — {licences.Wizzyl} licence
                              {licences.Wizzyl > 1 ? "s" : ""} × £49.99
                            </td>
                            <td className="border border-gray-600 p-3 text-right text-white font-semibold">
                              £{(licences.Wizzyl * 49.99).toFixed(2)}
                            </td>
                          </tr>
                        )}

                        {/* 6ELA */}
                        {licences["6ELA"] > 0 && (
                          <tr className="bg-gray-900">
                            <td className="border border-gray-600 p-3 font-semibold text-blue-300">
                              6ELA
                            </td>
                            <td className="border border-gray-600 p-3 text-gray-300 text-sm">
                              Invoice &amp; Credit Control — {licences["6ELA"]}{" "}
                              licence{licences["6ELA"] > 1 ? "s" : ""} × £49.99
                            </td>
                            <td className="border border-gray-600 p-3 text-right text-white font-semibold">
                              £{(licences["6ELA"] * 49.99).toFixed(2)}
                            </td>
                          </tr>
                        )}

                        {/* AXO Signal */}
                        {axo && (
                          <tr className="bg-gray-900">
                            <td className="border border-gray-600 p-3 font-semibold text-blue-300">
                              AXO Signal
                            </td>
                            <td className="border border-gray-600 p-3 text-gray-300 text-sm">
                              {axo.label ?? "AXO Signal"}
                              {axo.companiesPerMonth != null
                                ? ` — ${axo.companiesPerMonth.toLocaleString()} companies/mo`
                                : " — Unlimited lead data"}
                            </td>
                            <td className="border border-gray-600 p-3 text-right text-white font-semibold">
                              £{(axo.price ?? 0).toFixed(2)}
                            </td>
                          </tr>
                        )}

                        {/* Total row */}
                        <tr className="bg-blue-900">
                          <td
                            className="border border-gray-600 p-3 font-bold text-white"
                            colSpan={2}
                          >
                            TOTAL MONTHLY DIGITAL SERVICES COST
                          </td>
                          <td className="border border-gray-600 p-3 text-right font-bold text-blue-300 text-lg">
                            £{(ds?.totalNewDigitalCost ?? 0).toFixed(2)}
                          </td>
                        </tr>
                      </tbody>
                    </table>

                    {/* VAT note */}
                    <p className="text-xs text-gray-500 mt-2 text-right">
                      All prices shown exclude VAT
                    </p>
                  </div>
                );
              })()}
          </>
        )}
        {/* Notes section - only shown in PDF if notes exist */}
        {quotationNotes && (
          <div className="avoid-page-break mx-4 mb-6 mt-4">
            <div className="border border-blue-400 rounded-lg p-5 bg-gray-900">
              <h3 className="text-blue-400 font-bold text-sm uppercase tracking-wider mb-3">
                Additional Notes
              </h3>
              <div className="text-gray-200 text-sm leading-relaxed">
                {quotationNotes.split(/\r?\n/).map((line, i) =>
                  line.trim() === "" ? (
                    <div key={i} style={{ height: "0.75rem" }} />
                  ) : (
                    <p key={i} style={{ marginBottom: "0.25rem" }}>
                      {line}
                    </p>
                  ),
                )}
              </div>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className=" text-center text-gray-500 text-sm pt-8 no-break-after">
          <div className="text-center mb-8">
            <img
              src="/images/contactus.png"
              className="mx-auto w-full max-w-4xl h-auto rounded-lg shadow-lg"
              alt="Phone system illustration"
            />
          </div>
        </div>
      </div>

      <style jsx>{`
        /* New page break class for better control */
        .quotation-container *:last-child {
          page-break-after: auto !important;
          break-after: auto !important;
          -webkit-page-break-after: auto !important;
        }
        .new-page {
          page-break-before: always !important;
          break-before: page !important;
          -webkit-page-break-before: always !important;
        }

        @media print {
          .no-print {
            display: none !important;
          }

          /* Force color printing */
          * {
            print-color-adjust: exact !important;
            -webkit-print-color-adjust: exact !important;
            color-adjust: exact !important;
          }

          body,
          html {
            print-color-adjust: exact !important;
            -webkit-print-color-adjust: exact !important;
            color-adjust: exact !important;
          }

          /* Page break controls */
          .new-page {
            page-break-before: always !important;
            break-before: page !important;
            -webkit-page-break-before: always !important;
          }

          .page-break-after {
            page-break-after: always !important;
            break-after: page !important;
            -webkit-page-break-after: always !important;
          }
          .no-break-after {
            page-break-after: auto !important;
            break-after: auto !important;
            -webkit-page-break-after: auto !important;
          }

          /* Ensure background colors print */
          .bg-blue-800 {
            background-color: #1e40af !important;
            color: white !important;
          }

          .bg-blue-50 {
            background-color: #eff6ff !important;
          }

          .bg-green-50 {
            background-color: #f0fdf4 !important;
          }

          .bg-green-100 {
            background-color: #dcfce7 !important;
          }

          .bg-blue-100 {
            background-color: #dbeafe !important;
          }

          .bg-gray-100 {
            background-color: #f3f4f6 !important;
          }

          /* Dark theme colors for print */
          .bg-black {
            background-color: #000000 !important;
            color: white !important;
          }

          .bg-gray-800 {
            background-color: #1f2937 !important;
            color: white !important;
          }

          .bg-gray-900 {
            background-color: #111827 !important;
            color: white !important;
          }

          .bg-gray-700 {
            background-color: #374151 !important;
            color: white !important;
          }

          .text-blue-400 {
            color: #60a5fa !important;
          }

          .text-green-400 {
            color: #4ade80 !important;
          }

          .border-gray-600 {
            border-color: #4b5563 !important;
          }

          /* Layout fixes */
          .print\\:flex {
            display: flex !important;
          }
          .print\\:flex-row {
            flex-direction: row !important;
          }
          .print\\:items-center {
            align-items: center !important;
          }
          .print\\:w-1\\/2 {
            width: 50% !important;
          }
          .print\\:w-1\\/3 {
            width: 33.333333% !important;
          }
          .print\\:w-2\\/3 {
            width: 66.666667% !important;
          }

          /* Print spacing classes */
          .print\\:mb-4 {
            margin-bottom: 1rem !important;
          }

          .print\\:mt-8 {
            margin-top: 2rem !important;
          }

          .print\\:gap-8 {
            gap: 2rem !important;
          }

          /* Prevent page breaks inside elements */
          .avoid-page-break {
            page-break-inside: avoid !important;
            break-inside: avoid !important;
          }

          /* Image sizing for print */
          .print\\:max-w-\\[200px\\] {
            max-width: 200px !important;
          }

          .print\\:max-w-\\[170px\\] {
            max-width: 170px !important;
          }

          .print\\:max-w-\\[180px\\] {
            max-width: 180px !important;
          }
        }

        /* Screen display - Add visual separation */
        @media screen {
          .new-page {
            margin-top: 2rem;
            padding-top: 2rem;
            position: relative;
          }

          .new-page::after {
            position: absolute;
            top: -12px;
            left: 50%;
            transform: translateX(-50%);
            background: #3b82f6;
            color: white;
            padding: 4px 12px;
            border-radius: 4px;
            font-size: 12px;
            font-weight: bold;
          }
        }

        /* Text justification */
        .text-justify {
          text-align: justify;
        }
      `}</style>
    </div>
  );
};

export default TelecomQuotation;